import { darken, Theme, ThemeOptions } from '@mui/material'
import { grey } from '@mui/material/colors'

import { CARD_HEADER_MIN_HEIGHT } from './commonOptions'

export const lightOptions = {
  palette: {
    mode: 'light',
    secondary: {
      main: '#2D2D2D',
      contrastText: '#fff'
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF'
    },
    textColour: '#000000',
    breadCrumbBackground: '#FFFFFF',
    breadCrumbTextColor: '#2E3238',
    breadCrumbBorder: '#E3E5E8',
    operatorTileBackground: '#FFFFFF',
    accordionHeaderBackground: '#FFFFFF',
    operatorTileText: '#737D8C',
    contentContainerBackground: '#F3F3F1',
    operatorActionsText: '#2e3238',
    header: {
      topBar: {
        color: '#2E3238',
        backgroundColor: '#E3E5E8'
      },
      titleBar: {
        color: '#212121',
        backgroundColor: '#f4f5f6'
      }
    },
    matchStatsTableTextColor: '#2E3238',
    matchStatsTableSecondaryTextColor: '#454B54',
    inputLabel: '#0009',
    accordion: {
      sportTitle: '#2E3238',
      tournament: '#fafafa',
      match: '#fbfbfb',
      divider: '#5C6470',
      matchStatsInnerBackground: '#F8F8F8'
    },
    subtable: {
      headerRow: {
        backgroundColor: '#F6EFEF',
        'th:first-of-type': {
          pl: 11
        },
        'td:first-of-type': {}
      },
      contentRow: {
        backgroundColor: '#F8F8F8',
        'td:first-of-type': {
          pl: 11
        },
        'th:first-of-type': {}
      }
    },
    sportColors: {
      SOCCER: {
        color: '#b6e4f7ff',
        background: '#0a7bb8'
      },
      BASKETBALL: {
        color: 'rgba(197, 129, 243, 0.91)',
        background: '#2d0f42'
      },
      'TABLE-TENNIS': {
        color: '#819dd5ff',
        background: '#133583'
      },
      VOLLEYBALL: {
        color: '#a5005d',
        background: '#cd007433'
      },
      SNOOKER: {
        color: '#3a9305',
        background: '#4AB40633'
      },
      'AMERICAN-FOOTBALL': {
        color: '#026800',
        background: '#02850033'
      },
      'ICE-HOCKEY': {
        color: '#2d8b7a',
        background: '#6EEED833'
      },
      CRICKET: {
        color: '#8a6635',
        background: '#b5895533'
      },
      OTHER: {
        color: '#ffffffff',
        background: '#362e2e5a'
      }
    },
    platformColors: {
      ANDROID: {
        color: '#8fd7a1ff',
        background: '#318A46'
      },
      IOS: {
        color: '#EBEDF3',
        background: '#989A9D'
      },
      CHROME: {
        color: '#FBC209',
        background: '#B68D06'
      },
      FIREFOX: {
        color: '#f78851ff',
        background: '#B44C18'
      },
      IE: {
        color: '#38f7a4ff',
        background: '#009052'
      },
      SAFARI: {
        color: '#72b9f3ff',
        background: '#186BAD'
      },
      OTHER: {
        color: '#bdbdbd',
        background: '#7F7F7F'
      }
    },
    card: {
      header: {
        minHeight: CARD_HEADER_MIN_HEIGHT,
        backgroundColor: '#F5F5F5'
      }
    },
    productionReport: {
      actionButton: {
        backgroundColor: '#c1c1c1ff',
        hoverBackgroundColor: '#adadadff'
      }
    },
    dateRangePicker: {
      borderRadius: 50,
      rangeBackground: 'rgba(255, 77, 0, 0.15)'
    },
    statistics: {
      panel: {
        backgroundColor: '#F5F5F5',
        header: {
          minHeight: CARD_HEADER_MIN_HEIGHT,
          backgroundColor: '#EEEEEE'
        }
      },
      card: {
        backgroundColor: '#FFFFFF'
      }
    },
    charts: {
      lineChart: {
        component: {
          '.MuiAreaElement-root': {
            opacity: 0.8
          },
          '.MuiLineElement-root': {
            opacity: 0.8
          }
        },
        yAxis: {
          color: ['#E76A66', '#FEA46C']
        }
      }
    },
    phoneInput: {
      buttonBackground: '#F5F5F5',
      hoverButtonBackground: '#EEEEEE',
      flagBorder: '#D9D9D9'
    },
    horizontalTable: {
      header: {
        backgroundColor: '#EEEEEE'
      },
      border: {
        color: '#EEEEEE'
      }
    },
    golfColours: {
      eagle: '#e3717a',
      birdie: '#F5BBB7',
      bogey: '#b4d4ef',
      doubleBogey: '#67add9'
    }
  },
  typography: {
    caption: {
      color: grey[700]
    }
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
          color: theme.palette.common.black
        })
      }
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiSelect-root': {
            '&.Mui-disabled': {
              backgroundColor: darken(theme.palette.background.paper, 0.05)
            }
          }
        })
      }
    },

    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          scrollbarColor: '#C4C4C4 #F5F5F5',
          scrollbarWidth: 4,

          '&::-webkit-scrollbar': {
            backgroundColor: '#e0e0e0',
            width: 8,
            height: 8
          },

          '&::-webkit-scrollbar-thumb': {
            borderRadius: 4,
            backgroundColor: '#C4C4C4',
            minHeight: 24
          }
        }
      }
    },

    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-filledDefault': {
            backgroundColor: '#CBCBCB'
          }
        }
      }
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '4px'
        },
        icon: {
          alignSelf: 'center',
          color: '#212121 !important'
        },
        standardInfo: {
          fontSize: 14,
          backgroundColor: '#e0e0e0',
          color: '#212121'
        }
      }
    },

    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
          borderBottom: `1px solid ${darken(theme.palette.background.default, 0.2)}`,
          borderTop: `1px solid ${darken(theme.palette.background.default, 0.2)}`
        })
      }
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: darken(theme.palette.background.default, 0.15)
        })
      }
    },

    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.default
        })
      }
    },

    MuiCheckbox: {
      styleOverrides: {
        root: () => ({
          color: '#2D2D2D'
        })
      }
    },

    MuiButton: {
      variants: [
        {
          props: { variant: 'dashed' },
          style: ({ theme }: { theme: Theme }) => ({
            textTransform: 'none',
            border: '2px dashed #d3d3d3',
            backgroundColor: 'rgba(255,255,255,0)',
            color: theme.palette.grey[500],
            borderStyle: 'dashed',
            borderColor: theme.palette.grey[400],
            transition: 'all 0.175s ease-in-out',
            '&:hover': {
              backgroundColor: theme.palette.grey[200],
              color: theme.palette.grey[600],
              transition: 'all 0.175s ease-in-out'
            }
          })
        }
      ]
    }
  }
} satisfies ThemeOptions
